import { QueryClient, QueryFunction } from "@tanstack/react-query";

// Get API base URL from environment or detect deployment platform
const getApiBaseUrl = (): string => {
  // Check for explicit environment variable first (Netlify sets this)
  if (import.meta.env.VITE_API_URL) {
    console.log('Using API URL from environment:', import.meta.env.VITE_API_URL);
    return import.meta.env.VITE_API_URL;
  }
  
  // For Netlify deployment (also check for netlify.com domains)
  if (window.location.hostname.includes('netlify.app') || 
      window.location.hostname.includes('netlify.com') ||
      window.location.hostname.endsWith('.netlify.live')) {
    const netlifyApiUrl = '/.netlify/functions/api';
    console.log('Netlify deployment detected, using API URL:', netlifyApiUrl);
    return netlifyApiUrl;
  }
  
  // For Vercel deployment
  if (window.location.hostname.includes('vercel.app')) {
    console.log('Vercel deployment detected, using standard API routing');
    return "";
  }
  
  // Log environment info for debugging
  console.log('Environment:', {
    apiUrl: 'Not set (using default)',
    mode: import.meta.env.MODE,
    hostname: window.location.hostname,
    isNetlify: window.location.hostname.includes('netlify'),
    isVercel: window.location.hostname.includes('vercel'),
    envApiUrl: import.meta.env.VITE_API_URL
  });
  
  // For local development or other platforms
  return "";
};

// Helper to properly format API URLs
const formatApiUrl = (url: string): string => {
  const baseUrl = getApiBaseUrl();
  
  // If URL already includes the baseUrl or is an absolute URL, return as is
  if (url.startsWith('http') || (baseUrl && url.includes(baseUrl))) {
    return url;
  }

  // For Netlify Functions, we need special handling
  if (baseUrl.includes('/.netlify/functions/api') && url.startsWith('/api')) {
    // Convert /api/users to /.netlify/functions/api/users
    const newUrl = `${baseUrl}${url.substring(4)}`;
    console.log(`Netlify API URL conversion: ${url} → ${newUrl}`);
    return newUrl;
  }
  
  // Otherwise, join baseUrl and url (for local dev or other platforms)
  return baseUrl ? `${baseUrl}${url}` : url;
};

// Enhanced debugging for mobile deployment issues
function logApiRequest(url: string, formattedUrl: string, method = 'GET') {
  const isNetlify = window.location.hostname.includes('netlify.app');
  const isVercel = window.location.hostname.includes('vercel.app');
  // Always log in development or on production platforms
  const isDebugging = import.meta.env.MODE === 'development' || isNetlify || isVercel; 
  
  if (isDebugging) {
    const networkInfo = {
      originalUrl: url,
      formattedUrl,
      fetchPatched: typeof window.NETLIFY_FETCH_PATCHED !== 'undefined',
      netlifyApiUrl: window.NETLIFY_API_URL,
      environment: isNetlify ? 'Netlify' : isVercel ? 'Vercel' : import.meta.env.MODE,
      hostname: window.location.hostname,
      userAgent: navigator.userAgent,
      isMobile: /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
      time: new Date().toISOString()
    };
    
    // Enhanced logging for cities endpoint (our problem area)
    if (url.includes('simulation/cities')) {
      console.log(`📍 CITIES API REQUEST (${method}):`, networkInfo);
      // Create visual debug indicator on mobile
      if (isNetlify && networkInfo.isMobile) {
        const debugEl = document.createElement('div');
        debugEl.style.cssText = 'position:fixed;top:10px;left:10px;background:red;color:white;padding:5px;z-index:9999;font-size:12px;';
        debugEl.textContent = `API: ${method} ${url}`;
        document.body.appendChild(debugEl);
        setTimeout(() => debugEl.remove(), 3000);
      }
    } else if (url.includes('success-stories')) {
      console.log(`🔍 SUCCESS STORIES API REQUEST (${method}):`, networkInfo);
    } else {
      console.log(`API ${method} Request:`, networkInfo);
    }
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    const isNetlify = window.location.hostname.includes('netlify.app');
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Enhanced error logging for mobile Netlify issues
    const errorInfo = {
      url: res.url,
      status: res.status,
      statusText: res.statusText,
      error: text,
      isNetlify,
      isMobile,
      timestamp: new Date().toISOString()
    };
    
    console.error(`API Error (${res.status}):`, errorInfo);
    
    // Create visual error indicator on mobile Netlify for debugging
    if (isNetlify && isMobile) {
      const errorEl = document.createElement('div');
      errorEl.style.cssText = 'position:fixed;top:50px;left:10px;background:red;color:white;padding:10px;z-index:9999;font-size:12px;max-width:300px;';
      errorEl.innerHTML = `
        <strong>API Error ${res.status}</strong><br>
        URL: ${res.url}<br>
        Error: ${text}
      `;
      document.body.appendChild(errorEl);
      setTimeout(() => errorEl.remove(), 8000);
    }
    
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  // Format URL for different environments
  const formattedUrl = formatApiUrl(url);
  
  // Log API request for debugging
  logApiRequest(url, formattedUrl, method);
  
  try {
    const res = await fetch(formattedUrl, {
      method,
      headers: data ? { "Content-Type": "application/json" } : {},
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
    });

    await throwIfResNotOk(res);
    return res;
  } catch (error) {
    console.error(`API Request Failed (${method} ${url}):`, error);
    throw error;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Format URL for different environments
    const url = queryKey[0] as string;
    const formattedUrl = formatApiUrl(url);
    
    // Log API request for debugging
    logApiRequest(url, formattedUrl);
    
    try {
      const res = await fetch(formattedUrl, {
        credentials: "include",
      });

      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        return null;
      }

      await throwIfResNotOk(res);
      return await res.json();
    } catch (error) {
      // Don't log 401 errors when we're expecting them
      if (!(unauthorizedBehavior === "returnNull" && error instanceof Error && error.message.startsWith("401"))) {
        console.error(`Query Failed (${url}):`, error);
      }
      throw error;
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
